import { Dataset } from "./Datasets";
import { PropertyTranslator, IGenericRequestParametersTranslator, INamedEntityLabels } from "./GenericTranslator";
import {
  GenericEntityOrderBy,
  EntityConstants,
  IConfigFile,
  IEntityMinimalModel,
  IGenericEntity,
  IGenericRequestParameters,
  StringIndexedDict,
  INamedEntity,
  NamedEntityOrderBy,
} from "./GenericTypes";

export const customImportConstants: EntityConstants<CustomImport, CustomImportFilters> = {
  resource: "custom_imports",
  frontendIndexRoute: "custom_imports",
  entitySingular: "metadata mapping",
  entityPlural: "metadata mappings",
  icon: "settings",
};

export const customImportFieldLabels = {
  name: "Name",
};

export interface CustomImport extends IGenericEntity<string>, IConfigFile, INamedEntity {
  name: string;
  variables: {
    [property: string]: CustomImportVariable;
  };
  variableMappings: {
    [property: string]: EntityVariableMapping;
  };
  rules: CustomImportRule[];
}
export interface CustomImportSuggestions extends IEntityMinimalModel<CustomImport["id"]> {}

export type CustomImportSortingOptions = (typeof CustomImportSortingOptionsConsts)[number];

export interface CustomImportFilters extends IGenericRequestParameters<CustomImport, CustomImportSortingOptions> {}

export const CustomImportFiltersTranslator: PropertyTranslator<CustomImportFilters> = {
  ...IGenericRequestParametersTranslator,
} as const;

interface CustomImportVariable {
  extractor: CustomImportExtractorDefinition;
  preprocessors?: CustomImportPreprocessor[];
}

interface CustomImportExtractorDefinition {
  type: CustomImportExtractorType;
  parameters: {
    [property: string]: Object;
  };
}
type CustomImportExtractorType = "PATH" | "NAME" | "PARAMETERS" | "STATIC" | "NOTES" | "MERGE";

interface CustomImportPreprocessor {
  type: CustomImportPreprocessorType;
  parameters: {
    [property: string]: Object;
  };
}
type CustomImportPreprocessorType = "REGEXP" | "ARRAY";

interface EntityVariableMapping {
  entityType: EntityType;
  sourceVariables: { [property: string]: string };
  resolvedVariables?: { [property: string]: string };
  mappingType: EntityMappingType;
  createIfNotExists: boolean;
}
type EntityType = "PROJECT" | "SAMPLE" | "PERSON";
type EntityMappingType = "BY_NAME" | "BY_ID";

interface CustomImportRule {
  type: RuleType;
  target: AssignmentTarget;
  sourceVariable: string;
  data?: { [property: string]: object };
}
type RuleType = "SET" | "ADD" | "CLAIM";
type AssignmentTarget = "PROJECT" | "SAMPLE" | "OPERATOR" | "OWNER" | "OTHER" | "CUSTOM_FIELDS";

export const CustomImportSortingOptionsConsts = [...GenericEntityOrderBy, ...NamedEntityOrderBy] as const;

export const CustomImportFieldLabels: PropertyTranslator<CustomImport> = {
  ...INamedEntityLabels,
  id: "Meta data mapping ID",
  variables: "Variables",
  variableMappings: "Variable Mappings",
  rules: "Rules",
} as const;

type MappingResultAction = "CREATE_ENTITY" | "USE_EXISTING_ENTITY" | "NOT_FOUND";
interface MappingPlan {
  index: number;
  entityType: EntityType;
  action: MappingResultAction;
  sourceVariable: StringIndexedDict<string>;
  entity: IGenericEntity<number>;
}

interface CustomImportError {
  message: string;
  stackTrace: string;
}
export interface CustomImportResult {
  resolvedStringVariables: StringIndexedDict<string>;
  mappingResults: StringIndexedDict<MappingPlan>;
  mappedEntitiesForResponse: StringIndexedDict<object>;
  modifiedDataset: Dataset;
  error: CustomImportError;
  datasetId: Dataset["id"];
}

// Custom import logs

export const customImportLogConstants: EntityConstants<CustomImportLog, CustomImportLogFilters> = {
  resource: "custom_import_logs",
  frontendIndexRoute: "custom_import_logs",
  entitySingular: "metadata mapping log",
  entityPlural: "metadata mapping logs",
  icon: "info",
};

export interface CustomImportLog extends IGenericEntity {
  timestamp: string;
  datasetId: Dataset["id"];
  customImportId: CustomImport["id"];
  message: string;
  type: CustomImportLogType;
  payload: CustomImportResult;
}

export type CustomImportLogType = "Info" | "Warning" | "Error";

export interface CustomImportLogFilters
  extends IGenericRequestParameters<CustomImportLog, CustomImportLogSortingOptions> {
  datasetIds?: number[];
  customImportIds?: string[];
  types?: CustomImportLogType[];
  timestampFrom?: string;
  timestampTo?: string;
}

export const CustomImportLogSortingOptionsConsts = [...GenericEntityOrderBy, "TIMESTAMP", "TYPE", "DATASET"] as const;
export type CustomImportLogSortingOptions = (typeof CustomImportLogSortingOptionsConsts)[number];

export const CustomImportLogFiltersTranslator: PropertyTranslator<CustomImportLogFilters> = {
  ...IGenericRequestParametersTranslator,
  datasetIds: "Dataset IDs",
  customImportIds: "Custom Import IDs",
  types: "Types",
  timestampFrom: "Timestamp From",
  timestampTo: "Timestamp To",
} as const;

export const CustomImportLogFieldLabels: PropertyTranslator<CustomImportLog> = {
  ...INamedEntityLabels,
  id: "Metadata mapping log ID",
  datasetId: "Dataset",
  customImportId: "Custom Import",
  message: "Message",
  type: "Type",
  payload: "Payload",
  timestamp: "Timestamp",
} as const;
